import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import api from "../../utils/api";

const TIMEZONES = [
  { label: "Eastern", offset: -5 },
  { label: "Central", offset: -6 },
  { label: "Mountain", offset: -7 },
  { label: "Pacific", offset: -8 },
  { label: "UTC", offset: 0 },
];

const TimePatternsChart = ({ queryParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tzIdx, setTzIdx] = useState(0); // default Eastern

  useEffect(() => {
    setLoading(true);
    api
      .get(`/api/analytics/time-patterns?${queryParams}`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [queryParams]);

  // Shift hourly data by timezone offset
  const shiftedHourly = useMemo(() => {
    if (!data?.hourly) return [];
    const offset = TIMEZONES[tzIdx].offset;
    const shifted = new Array(24).fill(0);
    for (const h of data.hourly) {
      const utcHour = parseInt(h.hour, 10);
      let localHour = (utcHour + offset + 24) % 24;
      shifted[localHour] += h.count;
    }
    return shifted.map((count, i) => ({
      hour: `${String(i).padStart(2, "0")}:00`,
      count,
    }));
  }, [data, tzIdx]);

  const hourlyAvg = useMemo(() => {
    if (!shiftedHourly.length) return 0;
    return Math.round(shiftedHourly.reduce((s, h) => s + h.count, 0) / shiftedHourly.length);
  }, [shiftedHourly]);

  const dailyAvg = useMemo(() => {
    if (!data?.daily?.length) return 0;
    return Math.round(data.daily.reduce((s, d) => s + d.count, 0) / data.daily.length);
  }, [data]);

  if (loading) {
    return <div className="text-center py-4"><div className="spinner-border spinner-border-sm" role="status" /></div>;
  }

  if (!data) return <p className="text-muted text-center">No data</p>;

  return (
    <div>
      <div className="row">
        <div className="col-md-7">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 style={{ fontSize: "12px", fontWeight: 600, textTransform: "uppercase", color: "#94a3b8", marginBottom: 0 }}>
              Scans by Hour of Day
            </h6>
            <div className="d-flex gap-1">
              {TIMEZONES.map((tz, i) => (
                <button key={i} className={`btn btn-sm ${tzIdx === i ? "btn-tl-primary" : "btn-outline-secondary"}`} style={{ borderRadius: "8px", fontSize: "11px", padding: "2px 8px" }} onClick={() => setTzIdx(i)}>
                  {tz.label}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={shiftedHourly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Scans" />
              <ReferenceLine y={hourlyAvg} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `avg ${hourlyAvg.toLocaleString()}`, position: "right", fontSize: 10, fill: "#ef4444" }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="col-md-5">
          <h6 style={{ fontSize: "12px", fontWeight: 600, textTransform: "uppercase", color: "#94a3b8", marginBottom: "8px" }}>
            Scans by Day of Week
          </h6>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Scans" />
              <ReferenceLine y={dailyAvg} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `avg ${dailyAvg.toLocaleString()}`, position: "right", fontSize: 10, fill: "#ef4444" }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-center mt-2">
        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
          <span style={{ display: "inline-block", width: "20px", borderTop: "2px dashed #ef4444", verticalAlign: "middle", marginRight: "6px" }}></span>
          Average scans per period
        </span>
      </div>
    </div>
  );
};

export default TimePatternsChart;
